import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { ShieldCheck, AlertTriangle, KeyRound, Trash2, ExternalLink, Download, UserX } from "lucide-react";
import { toast } from "sonner";

const EXCHANGE_HELP = {
  coinbase: {
    url: "https://www.coinbase.com/settings/api",
    color: "#007AFF",
    desc: "Coinbase Advanced Trade. Generate an API key with View + Trade permissions.",
  },
  btcc: {
    url: "https://www.btcc.com/account/apikey",
    color: "#FFB300",
    desc: "BTCC trading API. Create an API key with read + trade scope, IP-whitelisted if possible.",
  },
  public: {
    url: "https://public.com",
    color: "#525252",
    desc: "Public.com does not offer a retail trading API yet. Keys are stored for future use.",
  },
  webull: {
    url: "https://developer.webullbroker.com",
    color: "#00B894",
    desc: "Webull OpenAPI is currently institutional-only in most regions. Keys are stored for future use.",
  },
  sofi: {
    url: "https://www.sofi.com",
    color: "#86F7F2",
    desc: "SoFi Invest does not currently offer a public retail trading API. Keys are stored for future use.",
  },
};

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [creds, setCreds] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/credentials");
      setCreds(r.data);
    } catch { /* ignore transient refresh failures */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  async function exportData() {
    try {
      const r = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `microtrade-export-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been exported");
    } catch {
      toast.error("Export failed");
    }
  }

  async function deleteAccount() {
    if (!window.confirm("PERMANENTLY delete your account and ALL associated data? This cannot be undone.")) return;
    if (!window.confirm("Final check: are you absolutely sure?")) return;
    try {
      await api.delete("/account");
      toast.success("Account deleted");
      await logout();
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="label-xs">Account</div>
        <h1 className="text-3xl font-[Chivo] tracking-tight">Settings</h1>
      </div>

      <div className="border border-[#FFB300]/30 bg-[#FFB300]/5 p-4" data-testid="settings-warning-banner">
        <div className="flex gap-3">
          <AlertTriangle size={18} className="text-[#FFB300] shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-[Chivo] text-[#FFB300]">Live Trading Risk Notice</div>
            <p className="text-neutral-300 mt-1">
              API keys you add below grant this app permission to <span className="text-white font-medium">place real orders</span> on
              your exchange account. Bots producing thousands of micro-trades can accumulate fees and
              losses rapidly. We strongly recommend:
            </p>
            <ul className="list-disc pl-5 mt-2 text-neutral-400 space-y-0.5 text-xs">
              <li>Create a dedicated sub-account / API key with <span className="text-white">trading-only</span> permissions (never enable withdraw).</li>
              <li>IP-whitelist the key whenever the exchange supports it.</li>
              <li>Start with very small trade sizes and frequencies until you trust the strategy.</li>
              <li>You can disable a key here instantly — it stops all live bots routing to that exchange.</li>
            </ul>
          </div>
        </div>
      </div>

      <h2 className="text-xl font-[Chivo] tracking-tight mt-8">Exchange Keys</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border" data-testid="exchange-cards">
        {creds.map((c) => (
          <ExchangeCard key={c.exchange} cred={c} onChange={refresh} />
        ))}
      </div>

      <div className="border border-border bg-[#0F0F0F] p-4 text-xs text-neutral-400 flex gap-3" data-testid="encryption-note">
        <ShieldCheck size={14} className="text-[#00E676]" />
        <span>Keys are encrypted at rest using Fernet (AES-128-CBC with HMAC-SHA256). The plain key is never written to logs or returned by the API after submission.</span>
      </div>

      <h2 className="text-xl font-[Chivo] tracking-tight mt-12">Account &amp; Privacy</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border" data-testid="account-cards">
        <div className="bg-[#0F0F0F] p-5 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Download size={16} className="text-[#007AFF]" />
            <span className="font-[Chivo] text-lg">Export My Data</span>
          </div>
          <p className="text-xs text-neutral-400">
            Download a complete JSON export of all data we hold about you — account, portfolio, bots, trades, and payment metadata.
            Per GDPR / CCPA, this is your right to data portability.
          </p>
          <Button
            data-testid="export-data-btn"
            onClick={exportData}
            variant="outline"
            className="border-border bg-transparent hover:bg-[#141414] mt-auto"
          >
            <Download size={14} className="mr-2" /> Download Export
          </Button>
        </div>

        <div className="bg-[#0F0F0F] p-5 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <UserX size={16} className="text-[#FF3B30]" />
            <span className="font-[Chivo] text-lg">Delete My Account</span>
          </div>
          <p className="text-xs text-neutral-400">
            Permanently delete your account, portfolio, bots, trade history, exchange keys, and payment records.
            <span className="text-[#FF3B30]"> This action cannot be undone.</span>
          </p>
          <Button
            data-testid="delete-account-btn"
            onClick={deleteAccount}
            variant="outline"
            className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 bg-transparent mt-auto"
            disabled={user?.role === "admin"}
            title={user?.role === "admin" ? "Admin accounts cannot self-delete" : ""}
          >
            <UserX size={14} className="mr-2" /> Delete Account
          </Button>
        </div>
      </div>
    </div>
  );
}

function ExchangeCard({ cred, onChange }) {
  const meta = EXCHANGE_HELP[cred.exchange] || {};
  const [editing, setEditing] = useState(!cred.has_secret);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!apiKey.trim() || !apiSecret.trim()) {
      toast.error("API key and secret are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/credentials", { exchange: cred.exchange, api_key: apiKey, api_secret: apiSecret });
      toast.success(`${cred.label} keys saved`);
      setApiKey(""); setApiSecret(""); setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove ${cred.label} API keys? Live bots on this exchange will stop being able to start.`)) return;
    try {
      await api.delete(`/credentials/${cred.exchange}`);
      toast.success("Keys removed");
      onChange();
      setEditing(true);
    } catch { toast.error("Remove failed"); }
  }

  return (
    <div className="bg-[#0F0F0F] p-5 flex flex-col gap-3" data-testid={`exchange-card-${cred.exchange}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound size={16} style={{ color: meta.color || "#fff" }} />
          <span className="font-[Chivo] text-lg">{cred.label}</span>
        </div>
        {cred.has_secret ? (
          <span className="text-xs px-2 py-0.5 border border-[#00E676] text-[#00E676]" data-testid={`status-${cred.exchange}`}>● CONNECTED</span>
        ) : (
          <span className="text-xs px-2 py-0.5 border border-neutral-700 text-neutral-400" data-testid={`status-${cred.exchange}`}>○ NOT CONNECTED</span>
        )}
      </div>

      <p className="text-xs text-neutral-400">{meta.desc}</p>
      {cred.note && <p className="text-xs text-[#FFB300]">{cred.note}</p>}

      {!editing && cred.has_secret ? (
        <>
          <div>
            <div className="label-xs">API Key</div>
            <div className="num text-sm mt-1">{cred.api_key_masked}</div>
          </div>
          <div className="text-xs text-neutral-500">
            Updated {cred.updated_at ? new Date(cred.updated_at).toLocaleString() : "—"}
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              data-testid={`edit-keys-${cred.exchange}`}
              variant="outline" className="border-border bg-transparent hover:bg-[#141414] flex-1"
              onClick={() => setEditing(true)}
            >
              Replace
            </Button>
            <Button
              data-testid={`delete-keys-${cred.exchange}`}
              variant="outline" className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 bg-transparent"
              onClick={remove}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </>
      ) : (
        <>
          <div>
            <Label className="label-xs">API Key</Label>
            <Input
              data-testid={`input-key-${cred.exchange}`}
              className="bg-[#0A0A0A] border-border mt-1 focus:ring-1 focus:ring-[#007AFF]"
              value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off"
              placeholder="Paste your API key"
            />
          </div>
          <div>
            <Label className="label-xs">API Secret</Label>
            <Input
              data-testid={`input-secret-${cred.exchange}`}
              type="password"
              className="bg-[#0A0A0A] border-border mt-1 focus:ring-1 focus:ring-[#007AFF]"
              value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} autoComplete="off"
              placeholder="Paste your API secret"
            />
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              data-testid={`save-keys-${cred.exchange}`}
              onClick={save} disabled={saving}
              className="bg-[#007AFF] hover:bg-[#3395FF] flex-1"
            >
              {saving ? "Saving…" : "Save Keys"}
            </Button>
            {cred.has_secret && (
              <Button variant="outline" className="border-border bg-transparent hover:bg-[#141414]" onClick={() => { setEditing(false); setApiKey(""); setApiSecret(""); }}>
                Cancel
              </Button>
            )}
          </div>
          {meta.url && (
            <a href={meta.url} target="_blank" rel="noreferrer" className="text-xs text-[#007AFF] hover:underline inline-flex items-center gap-1 mt-1">
              Get keys at {cred.label} <ExternalLink size={11} />
            </a>
          )}
        </>
      )}
    </div>
  );
}
